import { get } from "lodash"
import { hasIdioWeightingRule, isIntradaySimulationEligible } from "../helpers"
import { percentageFormatter } from "helpers/formatters/numbers"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"

export const TOTAL_RISK = "risk-total"
export const FACTORS_RISK = "risk-factors"
export const SPECIFIC_RISK = "risk-specific"

export const METRICS = {
  [TOTAL_RISK]: {
    label: "Total",
    path: "total",
    formatter: percentageFormatter,
  },
  [FACTORS_RISK]: {
    label: "Factors",
    path: "attribution.summary.factors",
    formatter: percentageFormatter,
  },
  [SPECIFIC_RISK]: {
    label: "Specific",
    path: "attribution.summary.specific",
    formatter: percentageFormatter,
  },
}

function getRiskSubquery(riskArgs, dateField) {
  return `risk${riskArgs} {
        ${dateField}
        total
        attribution {
          summary {
            factors
            specific
          }
          factors {
            id
            category
            name
            value
          }
        }
      }`
}

function buildQuery(shouldUseIntradayQuery) {
  if (shouldUseIntradayQuery) {
    // ModelIntradaySimulation.risk takes no skipCompositeExpansion arg, and IntradayRisk exposes
    // `timestamp: DateTime!` not `date` — alias it so downstream select stays uniform
    const riskSubquery = getRiskSubquery("", "date: timestamp")
    return `#multi-date-risk
query MultiDateRiskQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!) {
  model(id: $modelId) {
    simulation:intradaySimulation(positionSet: $positionSet, base: $base, on: $on) {
      ${riskSubquery}
    }
  }
}`
  }

  const riskSubquery = getRiskSubquery("(skipCompositeExpansion: $skipCompositeExpansion)", "date")
  return `#multi-date-risk
query MultiDateRiskQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $skipCompositeExpansion: Boolean) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      ${riskSubquery}
    }
  }
}`
}

const DAY_BASED_QUERY = buildQuery(false)
const INTRADAY_QUERY = buildQuery(true)

export function createRiskMultiDateQuery(modelId, positionSet, base, from, to, isIntradayMode, options = {}) {
  const skipCompositeExpansion = hasIdioWeightingRule(positionSet)
  const shouldUseIntradaySimulation = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)

  const key = generateQueryKey("risk.multiDate", {
    modelId, positionSet, base, from, to,
    // ModelIntradaySimulation.risk doesn't take skipCompositeExpansion, so it can't affect the
    // result on this branch — leaving it in the key would split cache entries that are identical.
    skipComposite: shouldUseIntradaySimulation ? undefined : (skipCompositeExpansion || undefined),
    intraday: shouldUseIntradaySimulation || undefined,
  })

  const sharedVariables = { modelId, positionSet, base }

  const variables = shouldUseIntradaySimulation
    ? { ...sharedVariables, on: from }
    : { ...sharedVariables, from, to, skipCompositeExpansion }

  return {
    key,
    query: shouldUseIntradaySimulation ? INTRADAY_QUERY : DAY_BASED_QUERY,
    variables,
    select: (graphqlResult) => {
      const risk = get(graphqlResult, "data.model.simulation.risk", [])
      return ({
        values: {
          formatter: percentageFormatter,
          // The code to map the dates data deliberately does not use lodash functions to improve the performance
          dates: risk.map(({ date, ...dateData }) => {
            const metrics = Object.entries(METRICS).reduce((acc, [metricId, metric]) => {
              acc[metricId] = get(dateData, metric.path)
              return acc
            }, {})

            const factors = dateData.attribution.factors.reduce((acc, { id, category, value }) => {
              acc[id] = value
              acc[category] = (acc[category] ?? 0) + value
              return acc
            }, {})

            return {
              date,
              ...metrics,
              ...factors,
            }
          }),
        },
      })
    },
  }
}
